import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Pencil, X } from "lucide-react";

const COVERAGE_TIERS = [
  { value: "employee_only", label: "Employee only" },
  { value: "employee_spouse", label: "Employee + Spouse" },
  { value: "employee_children", label: "Employee + Children" },
  { value: "family", label: "Family" },
  { value: "waived", label: "Waived" },
  { value: "unknown", label: "Unknown" },
] as const;

const STATUSES = [
  { value: "enrolled", label: "Enrolled" },
  { value: "waived", label: "Waived" },
  { value: "terminated", label: "Terminated" },
] as const;

export interface EditableEnrollment {
  rowNumber?: number;
  personFirstName?: string;
  personLastName?: string;
  ownerEmployeeFirstName?: string;
  ownerEmployeeLastName?: string;
  ownerEmployeeEid?: string | null;
  isDependent?: boolean;
  relationship?: string;
  planDisplayName?: string;
  easePlanType?: string;
  status?: "enrolled" | "waived" | "terminated";
  coverageTier?:
    | "employee_only"
    | "employee_spouse"
    | "employee_children"
    | "family"
    | "waived"
    | "unknown";
  coverageDetails?: string;
  waiverReason?: string | null;
  effectiveDate?: string | null;
  processedDate?: string | null;
  coverageAmount?: number | null;
  totalRate?: number | null;
  employeeRate?: number | null;
  spouseRate?: number | null;
  childrenRate?: number | null;
  employeeContribution?: number | null;
  employeePreTaxCost?: number | null;
  employeePostTaxCost?: number | null;
  employeeCostPerDeduction?: number | null;
  employerContribution?: number | null;
  employerCostPerDeduction?: number | null;
  [k: string]: unknown;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobId: number;
  enrollments: EditableEnrollment[];
  index: number;
}

function str(v: string | null | undefined): string {
  return v == null ? "" : String(v);
}

function num(v: number | null | undefined): string {
  return v == null ? "" : String(v);
}

function fmtMoney(v: number | null | undefined): string {
  return v == null ? "—" : `$${v.toFixed(2)}`;
}

export function EditEnrollmentModal({
  open,
  onOpenChange,
  jobId,
  enrollments,
  index,
}: Props) {
  const { toast } = useToast();
  const qc = useQueryClient();

  const initial = enrollments[index] ?? ({} as EditableEnrollment);
  const [draft, setDraft] = useState<EditableEnrollment>(initial);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (open) {
      setDraft(enrollments[index] ?? ({} as EditableEnrollment));
      setIsEditing(false);
    }
  }, [open, index, enrollments]);

  const save = useMutation({
    mutationFn: async () => {
      const next = enrollments.map((e, i) => (i === index ? draft : e));
      const res = await apiRequest(
        "PATCH",
        `/api/migration/jobs/${jobId}/enrollments`,
        { enrollments: next },
      );
      return await res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [`/api/migration/jobs/${jobId}`] });
      toast({ title: "Enrollment saved" });
      setIsEditing(false);
    },
    onError: (err: any) =>
      toast({
        title: "Save failed",
        description: err?.message ?? "Unknown error",
        variant: "destructive",
      }),
  });

  function set<K extends keyof EditableEnrollment>(
    field: K,
    value: EditableEnrollment[K],
  ) {
    setDraft((prev) => ({ ...prev, [field]: value }));
  }

  function setStr<K extends keyof EditableEnrollment>(field: K) {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = e.target.value;
      set(field, (v === "" ? null : v) as EditableEnrollment[K]);
    };
  }

  function setNum<K extends keyof EditableEnrollment>(field: K) {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = e.target.value;
      set(
        field,
        (v === "" ? null : Number(v)) as EditableEnrollment[K],
      );
    };
  }

  const e = isEditing ? draft : (enrollments[index] ?? ({} as EditableEnrollment));
  const tierLabel =
    COVERAGE_TIERS.find((t) => t.value === e.coverageTier)?.label ||
    str(e.coverageTier);
  const statusLabel =
    STATUSES.find((s) => s.value === e.status)?.label || str(e.status);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] max-w-3xl flex-col gap-0 p-0">
        <DialogHeader className="border-b p-6 pb-4">
          <DialogTitle>
            {e.personFirstName} {e.personLastName} ·{" "}
            <span className="font-normal">{e.planDisplayName}</span>
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? "Editing the migration staging copy. If enrollments is already confirmed, edits are written through to the imported enrollment row."
              : "Full enrollment row from the source file. Click Edit below to make changes."}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 space-y-6 overflow-y-auto p-6">
          {/* Identity (read-only context) */}
          <Section title="Person & plan (read-only)">
            <Field label="Person" display={`${e.personFirstName ?? ""} ${e.personLastName ?? ""}`} editing={false} />
            <Field label="Relationship" display={str(e.relationship)} editing={false} />
            <Field
              label="Owner employee"
              display={`${e.ownerEmployeeFirstName ?? ""} ${e.ownerEmployeeLastName ?? ""}`}
              editing={false}
            />
            <Field label="Plan" display={str(e.planDisplayName)} editing={false} />
            <Field
              label="Plan type (source)"
              display={str(e.easePlanType)}
              editing={false}
            />
          </Section>

          {/* Election */}
          <Section title="Election">
            <Field
              label="Status"
              editing={isEditing}
              display={statusLabel}
            >
              <Select
                value={str(draft.status) || "enrolled"}
                onValueChange={(v) => set("status", v as any)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field
              label="Coverage tier"
              editing={isEditing}
              display={tierLabel}
            >
              <Select
                value={str(draft.coverageTier) || "employee_only"}
                onValueChange={(v) => set("coverageTier", v as any)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COVERAGE_TIERS.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field
              label="Effective date"
              editing={isEditing}
              display={str(e.effectiveDate)}
            >
              <Input
                type="date"
                value={str(draft.effectiveDate)}
                onChange={setStr("effectiveDate")}
              />
            </Field>
            <Field
              label="Processed date"
              editing={isEditing}
              display={str(e.processedDate)}
            >
              <Input
                type="date"
                value={str(draft.processedDate)}
                onChange={setStr("processedDate")}
              />
            </Field>
            {e.status === "waived" && (
              <Field
                label="Waive reason"
                editing={isEditing}
                display={str(e.waiverReason)}
                full
              >
                <Input
                  value={str(draft.waiverReason)}
                  onChange={setStr("waiverReason")}
                />
              </Field>
            )}
            <Field
              label="Coverage details"
              editing={isEditing}
              display={str(e.coverageDetails)}
              full
            >
              <Input
                value={str(draft.coverageDetails)}
                onChange={setStr("coverageDetails")}
              />
            </Field>
            <Field
              label="Coverage amount (life face value / FSA election)"
              editing={isEditing}
              display={fmtMoney(e.coverageAmount)}
              full
            >
              <Input
                type="number"
                step="0.01"
                value={num(draft.coverageAmount)}
                onChange={setNum("coverageAmount")}
              />
            </Field>
          </Section>

          {/* Rates */}
          <Section title="Rates (monthly)">
            <Field
              label="Total rate"
              editing={isEditing}
              display={fmtMoney(e.totalRate)}
            >
              <Input
                type="number"
                step="0.01"
                value={num(draft.totalRate)}
                onChange={setNum("totalRate")}
              />
            </Field>
            <Field
              label="Employee rate"
              editing={isEditing}
              display={fmtMoney(e.employeeRate)}
            >
              <Input
                type="number"
                step="0.01"
                value={num(draft.employeeRate)}
                onChange={setNum("employeeRate")}
              />
            </Field>
            <Field
              label="Spouse rate"
              editing={isEditing}
              display={fmtMoney(e.spouseRate)}
            >
              <Input
                type="number"
                step="0.01"
                value={num(draft.spouseRate)}
                onChange={setNum("spouseRate")}
              />
            </Field>
            <Field
              label="Children rate"
              editing={isEditing}
              display={fmtMoney(e.childrenRate)}
            >
              <Input
                type="number"
                step="0.01"
                value={num(draft.childrenRate)}
                onChange={setNum("childrenRate")}
              />
            </Field>
          </Section>

          {/* Contributions */}
          <Section title="Contributions (per pay period unless noted)">
            <Field
              label="Employee contribution (monthly)"
              editing={isEditing}
              display={fmtMoney(e.employeeContribution)}
            >
              <Input
                type="number"
                step="0.01"
                value={num(draft.employeeContribution)}
                onChange={setNum("employeeContribution")}
              />
            </Field>
            <Field
              label="Employer contribution (monthly)"
              editing={isEditing}
              display={fmtMoney(e.employerContribution)}
            >
              <Input
                type="number"
                step="0.01"
                value={num(draft.employerContribution)}
                onChange={setNum("employerContribution")}
              />
            </Field>
            <Field
              label="Employee cost per deduction"
              editing={isEditing}
              display={fmtMoney(e.employeeCostPerDeduction)}
            >
              <Input
                type="number"
                step="0.01"
                value={num(draft.employeeCostPerDeduction)}
                onChange={setNum("employeeCostPerDeduction")}
              />
            </Field>
            <Field
              label="Employer cost per deduction"
              editing={isEditing}
              display={fmtMoney(e.employerCostPerDeduction)}
            >
              <Input
                type="number"
                step="0.01"
                value={num(draft.employerCostPerDeduction)}
                onChange={setNum("employerCostPerDeduction")}
              />
            </Field>
            <Field
              label="Pre-tax cost"
              editing={isEditing}
              display={fmtMoney(e.employeePreTaxCost)}
            >
              <Input
                type="number"
                step="0.01"
                value={num(draft.employeePreTaxCost)}
                onChange={setNum("employeePreTaxCost")}
              />
            </Field>
            <Field
              label="Post-tax cost"
              editing={isEditing}
              display={fmtMoney(e.employeePostTaxCost)}
            >
              <Input
                type="number"
                step="0.01"
                value={num(draft.employeePostTaxCost)}
                onChange={setNum("employeePostTaxCost")}
              />
            </Field>
          </Section>
        </div>

        <DialogFooter className="border-t bg-background p-4">
          {isEditing ? (
            <>
              <Button
                variant="ghost"
                onClick={() => {
                  setIsEditing(false);
                  setDraft(enrollments[index] ?? ({} as EditableEnrollment));
                }}
                disabled={save.isPending}
              >
                <X className="mr-1 h-3 w-3" /> Cancel
              </Button>
              <Button onClick={() => save.mutate()} disabled={save.isPending}>
                {save.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Save
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Close
              </Button>
              <Button onClick={() => setIsEditing(true)}>
                <Pencil className="mr-1 h-3 w-3" /> Edit
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-3">
      <h3 className="border-b pb-1 text-sm font-semibold text-muted-foreground">
        {title}
      </h3>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">{children}</div>
    </div>
  );
}

function Field({
  label,
  children,
  editing,
  display,
  full,
}: {
  label: string;
  children?: React.ReactNode;
  editing?: boolean;
  display?: React.ReactNode;
  full?: boolean;
}) {
  return (
    <div className={`space-y-1 ${full ? "md:col-span-2" : ""}`}>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      {editing ? (
        children
      ) : (
        <div className="text-sm">
          {display !== undefined && display !== null && display !== "" ? (
            display
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </div>
      )}
    </div>
  );
}
